import React from 'react'

import { Controller, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { RegisterRequestDTO } from '@/core/api/dtos/request/auth/register'
import { BusinessCategory } from '@/core/api/entities/businessCategory'
import { Button } from '@/shared/components/atoms/Button'
import { ErrorMessage } from '@/shared/components/atoms/ErrorMessage'
import { Label } from '@/shared/components/atoms/Label/Label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/atoms/Select'
import { Textarea } from '@/shared/components/atoms/TextArea'
import { sortSubcategories } from '@/shared/utils/businessCategoryUtils'

interface RegisterStep5Props {
	form: UseFormReturn<RegisterRequestDTO>
	businessCategories: BusinessCategory[]
	isLoading: boolean
	onNextStep: (data: Partial<RegisterRequestDTO>) => void
	onPrevStep: () => void
}

export const RegisterStep5: React.FC<RegisterStep5Props> = ({
	form,
	businessCategories,
	isLoading,
	onNextStep,
}) => {
	const { t } = useTranslation('auth')

	const businessCategoryId = form.watch('business_category_id')
	const businessCategoryDescription = form.watch(
		'business_category_description'
	)

	const filteredSubcategories = sortSubcategories(
		businessCategories.filter(
			(c) =>
				c.parentId !== null &&
				c.parentId !== undefined &&
				!c.tags?.includes('hide')
		)
	)

	const selectedSubcategory = businessCategories.find(
		(c) => c.id === businessCategoryId
	)
	const requiresDescription = selectedSubcategory?.requiresDescription ?? false

	const isFormValid =
		!!businessCategoryId &&
		(!requiresDescription ||
			(typeof businessCategoryDescription === 'string' &&
				businessCategoryDescription.trim().length > 0 &&
				businessCategoryDescription.length <= 100))

	const handleNextStep = async () => {
		const baseFields = [
			'parent_business_category_id',
			'business_category_id',
		] as const
		const allFields = requiresDescription
			? ([...baseFields, 'business_category_description'] as const)
			: baseFields

		const isValid = await form.trigger(allFields as any)

		if (isValid) {
			const values = form.getValues()
			onNextStep({
				parent_business_category_id: values.parent_business_category_id,
				business_category_id: values.business_category_id,
				business_category_description: requiresDescription
					? values.business_category_description
					: undefined,
			})
		}
	}

	return (
		<div className="space-y-6 flex flex-col justify-between h-full">
			<div>
				<h1 className="register-title font-bold  mb-4">
					{t('registerStep4.title')}
				</h1>
				<p className="mb-8">{t('registerStep4.subtitle')}</p>
				<div className="space-y-4">
					<div>
						<Label className="mb-2 block">
							{t('register.businessCategory')}
						</Label>
						<Controller
							control={form.control}
							name="business_category_id"
							render={({ field, fieldState: { error } }) => (
								<div>
									<Select
										value={field.value ? String(field.value) : ''}
										onValueChange={(value) => {
											const category = businessCategories.find(
												(c) => c.id === Number(value)
											)
											form.setValue(
												'parent_business_category_id',
												category?.parentId ?? 0,
												{ shouldValidate: true }
											)
											form.setValue('business_category_description', undefined)
											field.onChange(Number(value))
										}}
									>
										<SelectTrigger
											className="h-10 w-full"
											data-testid="subcategory-select"
										>
											<SelectValue
												placeholder={t('register.businessCategory')}
											/>
										</SelectTrigger>
										<SelectContent>
											{filteredSubcategories.map((category) => (
												<SelectItem
													key={category.id}
													value={String(category.id)}
												>
													{category.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									{error && <ErrorMessage error={error.message ?? ''} />}
								</div>
							)}
						/>
					</div>

					{requiresDescription && (
						<div>
							<Label className="mb-2 block">
								{t('registerStep4.businessCategoryDescription')}
							</Label>
							<p className="text-sm text-muted-foreground mb-2">
								{t('registerStep4.businessCategoryDescriptionHelp')}
							</p>
							<Controller
								control={form.control}
								name="business_category_description"
								render={({ field, fieldState: { error } }) => (
									<div>
										<Textarea
											{...field}
											value={field.value ?? ''}
											placeholder={t(
												'registerStep4.businessCategoryDescriptionPlaceholder'
											)}
											maxLength={100}
											rows={3}
										/>
										<div className="flex justify-between mt-1">
											{error ? (
												<ErrorMessage error={error.message ?? ''} />
											) : (
												<span />
											)}
											<span className="text-xs text-muted-foreground ml-auto">
												{(field.value ?? '').length}/100
											</span>
										</div>
									</div>
								)}
							/>
						</div>
					)}
				</div>
			</div>

			<div className="mt-8 flex gap-4">
				<Button
					onClick={handleNextStep}
					className="px-8 py-3 cursor-pointer flex-1 h-10 text-sm font-medium rounded-full"
					disabled={isLoading || !isFormValid}
				>
					{isLoading ? 'Loading...' : t('register.following')}
				</Button>
			</div>
		</div>
	)
}
